import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { StoreProduct } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';

import { displayTitle } from '../api/mappers';
import { useRemoveStoreProduct } from '../hooks/use-store-product-mutations';

export interface RemoveStoreProductDialogProps {
  storeId: string;
  row: StoreProduct | null;
  onOpenChange: (open: boolean) => void;
  onRemoved?: () => void;
}

/** Confirms taking a product out of the store. Overrides go with it; master product and store prices stay. */
export function RemoveStoreProductDialog({
  storeId,
  row,
  onOpenChange,
  onRemoved,
}: RemoveStoreProductDialogProps) {
  const { t } = useTranslation(['storeCatalog', 'common', 'errors']);
  const remove = useRemoveStoreProduct(storeId);
  const message = useErrorMessage();
  const requestId = isApiError(remove.error) ? remove.error.requestId : undefined;

  return (
    <AlertDialog
      open={row !== null}
      onOpenChange={(next) => {
        if (!next) remove.reset();
        onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('storeCatalog:remove.title', { title: row ? displayTitle(row) : '' })}
          </AlertDialogTitle>
          <AlertDialogDescription>{t('storeCatalog:remove.description')}</AlertDialogDescription>
        </AlertDialogHeader>
        {remove.isError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>
              {message(remove.error)}
              {requestId ? (
                <span className="block text-xs">{t('errors:requestIdShort', { requestId })}</span>
              ) : null}
            </AlertDescription>
          </Alert>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={remove.isPending}>
            {t('common:actions.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={remove.isPending}
            onClick={(event) => {
              // Radix closes on click; stay open until the backend has answered.
              event.preventDefault();
              if (row) {
                remove.mutate(row.id, {
                  onSuccess: () => {
                    onOpenChange(false);
                    onRemoved?.();
                  },
                });
              }
            }}
          >
            {remove.isPending
              ? t('storeCatalog:remove.submitting')
              : t('storeCatalog:remove.submit')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
